import React, { useEffect, useState, useRef } from 'react';
import MaterialTable, { Query } from 'material-table';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { personHasRole } from '../../utils/personUtils';
import { PersonRole } from '../../models/eums/personRole';
import { TournamentCalendarModel } from '../../models/tournamentCalendarModel';
import {
  deleteTournamentReq,
  assignCandidate,
  removeCandidate,
} from '../../api/tournamentCalendar/editApi';
import { getTournamentCalendarReq } from '../../api/tournamentCalendar/listApi';
import { getFullName } from '../../utils/personUtils';
import { OptimisticTableFooter } from '../../components/optimisticDataTableFooter/optimisticTableFooter';
import { TableToolbar } from './tableToolbar';
import moment from 'moment';
import { showInfo } from '../../services/notification';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../stores';
import { localization } from 'config/localization';

interface RouterHistoryState {
  listResData: TournamentCalendarModel[];
  searchResData: TournamentCalendarModel[];
  listFromDate: string;
  pageSize: number;
  searchText?: string;
}

interface DataTableQuery extends Query<TournamentCalendarModel> {
  listFromDate: string;
}

export function TournamentCalendarView() {
  const { t } = useTranslation();
  const history = useHistory<RouterHistoryState>();
  const { state: hState } = history.location;

  const [isAdmin, setIsAdmin] = useState(false);
  const [listResData, setListResData] = useState<TournamentCalendarModel[]>(
    hState?.listResData || [],
  );
  const [searchResData, setSearchResData] = useState<TournamentCalendarModel[]>(
    hState?.searchResData || [],
  );
  const [searchText, setSearchText] = useState<string | undefined>(hState?.searchText);
  const [pageSize, setPageSize] = useState<number>(hState?.pageSize || 10);
  const [listFromDate, setListFromDate] = useState<string>(
    hState?.listFromDate || moment().format('YYYY-MM-DD'),
  );
  const dataTableRef = useRef<MaterialTable<TournamentCalendarModel>>(null);
  const currentPersonId = useSelector<ApplicationState, string | undefined>(
    state => state.auth.personId,
  );

  useEffect(() => {
    handleUserPermissions();
  }, [setIsAdmin]);

  function handleUserPermissions() {
    personHasRole(PersonRole.Admin) ? setIsAdmin(true) : setIsAdmin(false);
  }

  function handleOnRowClick(event?: React.MouseEvent, tournament?: TournamentCalendarModel) {
    if (tournament?._id) {
      history.replace(history.location.pathname, {
        listResData,
        searchResData,
        searchText,
        pageSize,
        listFromDate,
      });

      if (isAdmin) history.push(`/tournament-calendar/edit-tournament/${tournament._id}`);
      else history.push(`/tournament-calendar/tournament/${tournament._id}`);
    }
  }

  async function fetchData(query: DataTableQuery) {
    //Spreading query to new, mutable object as we need to change some values.
    let mQuery = { ...query };

    setPageSize(mQuery.pageSize);
    if (mQuery.listFromDate) setListFromDate(mQuery.listFromDate);
    else mQuery.listFromDate = listFromDate;

    if (mQuery.search) return searchData(mQuery);

    if (searchResData.length > 0) {
      setSearchResData([]);
      setSearchText(undefined);
    }

    return listData(mQuery);
  }

  async function listData(query: DataTableQuery) {
    let pagingDirection;
    let fromSorterId;

    if (listResData.length > 0) {
      switch (query.page) {
        case 1:
          pagingDirection = 'next';
          fromSorterId = listResData[listResData.length - 1].sorterId;
          break;
        case -1:
          pagingDirection = 'prev';
          fromSorterId = listResData[0].sorterId;
          break;
        default:
          fromSorterId = listResData[0].sorterId;
          break;
      }
    }

    const qResponse = await getTournamentCalendarReq(
      pagingDirection,
      fromSorterId,
      query.listFromDate,
      query.search,
      query.pageSize,
    );

    let dataSet = listResData;
    if (qResponse?.data && qResponse.data.length > 0) {
      setListResData(qResponse.data);
      dataSet = qResponse.data;
    } else if (query.page) showInfo(t('end_of_collection'));

    return {
      data: dataSet,
      page: 0,
      totalCount: dataSet.length,
    };
  }

  async function searchData(query: DataTableQuery) {
    let pagingDirection;
    let fromSorterId;

    //If user does not change search text then we need to allow page listing within same search.
    //Otherwise ignore paging and start new search
    const sameSearchContext = query.search === searchText;
    if (searchResData.length > 0 && sameSearchContext) {
      switch (query.page) {
        case 1:
          pagingDirection = 'next';
          fromSorterId = searchResData[searchResData.length - 1].sorterId;
          break;
        case -1:
          pagingDirection = 'prev';
          fromSorterId = searchResData[0].sorterId;
          break;
        default:
          fromSorterId = searchResData[0].sorterId;
          break;
      }
    }

    const qResponse = await getTournamentCalendarReq(
      pagingDirection,
      fromSorterId,
      query.listFromDate,
      query.search,
      query.pageSize,
    );

    let dataSet = searchResData;
    if (query.page) {
      if (qResponse.data && qResponse.data.length > 0) {
        setSearchResData(qResponse.data);
        dataSet = qResponse.data;
      } else showInfo(t('end_of_collection'));
    } else {
      setSearchResData(qResponse.data);
      dataSet = qResponse.data;
    }

    setSearchText(query.search);

    return {
      data: dataSet,
      page: 0,
      totalCount: dataSet.length,
    };
  }

  function isPersonTournamentCandidate(tournament?: TournamentCalendarModel) {
    if (!tournament || !tournament.candidates) return false;

    const candidateIndex = tournament.candidates.findIndex(
      cn => cn.personUUID.personId === currentPersonId,
    );
    if (candidateIndex < 0) return false;

    return true;
  }

  //Changing search from date is considered as brand new data set.
  //On date change we must clear table data and initiate new search from desired date.
  function clearTableData() {
    setListResData([]);
    setSearchResData([]);
  }

  return (
    <>
      <MaterialTable
        options={{
          minBodyHeight: '80vh',
          debounceInterval: 500,
          headerStyle: {
            backgroundColor: '#F5F5F5',
            fontWeight: 'bold',
          },
          sorting: false,
          pageSize,
          searchText,
        }}
        localization={localization()}
        actions={[
          {
            hidden: !isAdmin,
            icon: 'delete',
            tooltip: t('delete'),
            onClick: async (event, rowData) => {
              const rd = rowData as TournamentCalendarModel;
              if (rd._id) {
                if (window.confirm(`${t('are_you_sure_delete')} ${rd.name}?`)) {
                  await deleteTournamentReq(rd._id);
                  (dataTableRef.current as any).onQueryChange();
                  showInfo(`${rd.name} deleted`);
                }
              }
            },
          },
          rowData => ({
            hidden: isPersonTournamentCandidate(rowData),
            icon: 'sports_tennis',
            tooltip: t('id_like_to_participate'),
            onClick: async (event, rowData) => {
              const rd = rowData as TournamentCalendarModel;
              if (rd._id) {
                await assignCandidate(rd._id);
                (dataTableRef.current as any).onQueryChange();
              }
            },
          }),
          rowData => ({
            hidden: !isPersonTournamentCandidate(rowData),
            icon: 'sports_tennis',
            iconProps: { style: { color: 'green' } },
            tooltip: t('changed_my_mind'),
            onClick: (event, rowData) => {
              const rd = rowData as TournamentCalendarModel;
              if (rd._id) {
                removeCandidate(rd._id);
                (dataTableRef.current as any).onQueryChange();
              }
            },
          }),
        ]}
        columns={[
          { title: t('name'), field: 'name', cellStyle: { textTransform: 'none' } },
          {
            title: t('start_date'),
            field: 'start',
            render: tc => tc.start && moment(tc.start).format('DD.MM.YYYY'),
          },
          {
            title: t('end_date'),
            field: 'end',
            render: tc => tc.end && moment(tc.end).format('DD.MM.YYYY'),
          },
          {
            title: t('category'),
            field: 'category',
          },
          {
            title: t('referee'),
            render: tc =>
              getFullName(
                tc.tournamentDirector?.preferredGivenName,
                tc.tournamentDirector?.preferredFamilyName,
              ),
          },
        ]}
        data={async q => fetchData(q as DataTableQuery)}
        title={t('tournament_calendar')}
        tableRef={dataTableRef}
        onRowClick={handleOnRowClick}
        components={{
          Toolbar: props => (
            <TableToolbar
              {...props}
              dataTableRef={dataTableRef.current}
              listFromDate={listFromDate}
              clearTableData={clearTableData}
            />
          ),
          Pagination: OptimisticTableFooter,
        }}
      />
    </>
  );
}
